import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  School, Calendar, Users, ChevronRight, ChevronLeft, 
  Plus, Minus, Check, Loader2, ArrowRight 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register: authRegister, token } = useAuth();
  
  // Flow state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // Selections
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [numChildren, setNumChildren] = useState(1);
  const [children, setChildren] = useState([{
    name: '',
    age: '',
    year_group: '',
    school_class: '',
    emergency_contact: '',
    medical_conditions: '',
    first_aid_permission: false,
    photo_permission: false,
    is_returning: false
  }]);
  
  // Auth state (for step 2 if not logged in)
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: ''
  });
  const [authLoading, setAuthLoading] = useState(false);

  const fetchSchools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/schools`);
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClasses = useCallback(async (schoolId) => {
    try {
      const response = await axios.get(`${API}/classes?school_id=${schoolId}`);
      setClasses(response.data.filter(c => !c.is_full));
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  }, []);

  useEffect(() => {
    fetchSchools();
    const preSelectedClass = searchParams.get('class');
    if (preSelectedClass) {
      setSelectedClass(preSelectedClass);
    }
  }, [searchParams, fetchSchools]);

  useEffect(() => {
    if (selectedSchool) {
      fetchClasses(selectedSchool);
    }
  }, [selectedSchool, fetchClasses]);

  useEffect(() => {
    // Adjust children array when numChildren changes
    if (numChildren > children.length) {
      const newChildren = [...children];
      for (let i = children.length; i < numChildren; i++) {
        newChildren.push({
          name: '',
          age: '',
          year_group: '',
          school_class: '',
          emergency_contact: '',
          medical_conditions: '',
          first_aid_permission: false,
          photo_permission: false,
          is_returning: false
        });
      }
      setChildren(newChildren);
    } else if (numChildren < children.length) {
      setChildren(children.slice(0, numChildren));
    }
  }, [numChildren, children.length]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      if (authMode === 'login') {
        await login(authForm.email, authForm.password);
        toast.success('Logged in successfully!');
        setStep(3);
      } else {
        if (authForm.password !== authForm.confirmPassword) {
          toast.error('Passwords do not match');
          setAuthLoading(false);
          return;
        }
        await authRegister(authForm.email, authForm.password, authForm.full_name, authForm.phone);
        toast.success('Account created successfully!');
        setStep(3);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleChildChange = (index, field, value) => {
    const newChildren = [...children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setChildren(newChildren);
  };

  const validateChildren = () => {
    for (let i = 0; i < children.length; i++) {
      if (!children[i].name || !children[i].age || !children[i].year_group || !children[i].emergency_contact) {
        toast.error(`Please complete all required fields for Child ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleEnroll = async () => {
    if (!validateChildren()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/simple-enroll`, {
        school_id: selectedSchool,
        class_id: selectedClass,
        num_children: numChildren,
        children: children.map(c => ({
          name: c.name,
          age: parseInt(c.age),
          year_group: parseInt(c.year_group),
          school_class: c.school_class,
          emergency_contact: c.emergency_contact,
          medical_conditions: c.medical_conditions,
          first_aid_permission: c.first_aid_permission,
          photo_permission: c.photo_permission
        })),
        returning_students: children.map(c => c.is_returning),
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
      setLoading(false);
    }
  };

  const RETURNING_PRICE = 62;
  const selectedClassData = classes.find(c => c.id === selectedClass);
  const calculateTotal = () => {
    if (!selectedClassData) return 0;
    const basePrice = selectedClassData.total_price;
    const siblingPrice = selectedClassData.sibling_total_price;
    
    let total = 0;
    children.forEach((child, idx) => {
      if (child.is_returning) {
        total += RETURNING_PRICE;
      } else if (idx === 0) {
        total += basePrice;
      } else {
        total += siblingPrice;
      }
    });
    return total;
  };

  if (loading && step === 1) {
    return (
      <div className="min-h-screen bg-artsy-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-artsy-background py-8 lg:py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${step >= 1 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>1</div>
            <span className={`font-body text-sm ${step >= 1 ? 'text-teal-600' : 'text-slate-500'}`}>Select Class</span>
          </div>
          <div className={`w-8 h-1 mx-2 ${step >= 2 ? 'bg-teal-600' : 'bg-slate-200'}`}></div>
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${step >= 2 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
            <span className={`font-body text-sm ${step >= 2 ? 'text-teal-600' : 'text-slate-500'}`}>{user ? 'Children' : 'Account'}</span>
          </div>
          <div className={`w-8 h-1 mx-2 ${step >= 3 ? 'bg-teal-600' : 'bg-slate-200'}`}></div>
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${step >= 3 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>3</div>
            <span className={`font-body text-sm ${step >= 3 ? 'text-teal-600' : 'text-slate-500'}`}>Details</span>
          </div>
        </div>

        <div className="card-plain">
          {/* Step 1: Select School and Class */}
          {step === 1 && (
            <div className="space-y-6" data-testid="enrollment-step-1">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Select School & Class</h2>
              
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-2">Select Your School *</label>
                <div className="grid gap-3">
                  {schools.map(school => (
                    <button
                      key={school.id}
                      onClick={() => {
                        setSelectedSchool(school.id);
                        setSelectedClass(null);
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedSchool === school.id 
                          ? 'border-teal-600 bg-teal-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`school-${school.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <School className={`w-6 h-6 ${selectedSchool === school.id ? 'text-teal-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-heading font-bold text-slate-900">{school.name}</p>
                          {school.address && <p className="font-body text-sm text-slate-500">{school.address}</p>}
                        </div>
                        {selectedSchool === school.id && <Check className="w-5 h-5 text-teal-600 ml-auto" />}
                      </div>
                    </button>
                  ))}
                </div>
                {schools.length === 0 && (
                  <p className="font-body text-slate-500 text-center py-4">No schools available. Please check back later.</p>
                )}
              </div>

              {selectedSchool && (
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Select Day & Time *</label>
                  {classes.length === 0 ? (
                    <p className="font-body text-slate-500 text-center py-4">No available classes at this school.</p>
                  ) : (
                    <div className="grid gap-3">
                      {classes.map(cls => (
                        <button
                          key={cls.id}
                          onClick={() => setSelectedClass(cls.id)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            selectedClass === cls.id 
                              ? 'border-teal-600 bg-teal-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`class-select-${cls.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Calendar className={`w-6 h-6 ${selectedClass === cls.id ? 'text-teal-600' : 'text-slate-400'}`} />
                              <div>
                                <p className="font-heading font-bold text-slate-900">{cls.day_of_week}s</p>
                                <p className="font-body text-sm text-slate-600">{cls.time}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-heading font-bold text-teal-600">£{cls.total_price}</p>
                              <p className="font-body text-xs text-slate-500">{cls.enrolled_count}/{cls.max_students} enrolled</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedClass && (
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Number of Children</label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setNumChildren(Math.max(1, numChildren - 1))}
                      className="w-10 h-10 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-teal-600"
                      disabled={numChildren <= 1}
                      data-testid="decrease-children"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="font-heading text-2xl font-bold text-slate-900">{numChildren}</span>
                    <button
                      onClick={() => setNumChildren(numChildren + 1)}
                      className="w-10 h-10 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-teal-600"
                      data-testid="increase-children"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {numChildren > 1 && (
                    <p className="font-body text-sm text-teal-600 mt-2">
                      <Check className="w-4 h-4 inline mr-1" />
                      Sibling discount applied: £{selectedClassData?.sibling_total_price}/child for additional children
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setStep(user ? 3 : 2)}
                  disabled={!selectedSchool || !selectedClass}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="continue-step-1"
                >
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Login/Register (only if not logged in) */}
          {step === 2 && !user && (
            <div className="space-y-6" data-testid="enrollment-step-2">
              <button 
                onClick={() => setStep(1)} 
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 font-body"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              <h2 className="font-heading text-2xl font-bold text-slate-900">
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </h2>

              <div className="flex gap-2">
                <button
                  onClick={() => setAuthMode('login')}
                  className={`flex-1 py-2 rounded-lg font-body font-semibold transition-colors ${
                    authMode === 'login' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setAuthMode('register')}
                  className={`flex-1 py-2 rounded-lg font-body font-semibold transition-colors ${
                    authMode === 'register' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  Create Account
                </button>
              </div>

              <form onSubmit={handleAuthSubmit} className="space-y-4">
                {authMode === 'register' && (
                  <>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={authForm.full_name}
                        onChange={(e) => setAuthForm({...authForm, full_name: e.target.value})}
                        className="input-artsy"
                        required
                        data-testid="auth-name"
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        value={authForm.phone}
                        onChange={(e) => setAuthForm({...authForm, phone: e.target.value})}
                        className="input-artsy"
                        required
                        data-testid="auth-phone"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    className="input-artsy"
                    required
                    data-testid="auth-email"
                  />
                </div>
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Password *</label>
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                    className="input-artsy"
                    required
                    data-testid="auth-password"
                  />
                </div>
                {authMode === 'register' && (
                  <div>
                    <label className="block font-body font-semibold text-slate-700 mb-1">Confirm Password *</label>
                    <input
                      type="password"
                      value={authForm.confirmPassword}
                      onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                      className="input-artsy"
                      required
                      data-testid="auth-confirm-password"
                    />
                  </div>
                )}
                <button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={authLoading}
                  data-testid="auth-submit"
                >
                  {authLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>Continue <ChevronRight className="w-5 h-5" /></>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Child Details */}
          {step === 3 && user && (
            <div className="space-y-6" data-testid="enrollment-step-3">
              <button 
                onClick={() => setStep(1)} 
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 font-body"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              <h2 className="font-heading text-2xl font-bold text-slate-900">Child Details</h2>
              <p className="font-body text-slate-600">Please provide details for each child you're enrolling.</p>

              {children.map((child, index) => (
                <div key={index} className="border-2 border-slate-200 rounded-xl p-4 space-y-4">
                  <h3 className="font-heading text-lg font-bold text-slate-900">
                    Child {index + 1} {index === 0 ? '' : '(Sibling)'}
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Child's Name *</label>
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                        className="input-artsy"
                        required
                        data-testid={`child-${index}-name`}
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Age *</label>
                      <input
                        type="number"
                        value={child.age}
                        onChange={(e) => handleChildChange(index, 'age', e.target.value)}
                        className="input-artsy"
                        min="4" max="12"
                        required
                        data-testid={`child-${index}-age`}
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Year Group *</label>
                      <select
                        value={child.year_group}
                        onChange={(e) => handleChildChange(index, 'year_group', e.target.value)}
                        className="input-artsy"
                        required
                        data-testid={`child-${index}-year`}
                      >
                        <option value="">Select year</option>
                        {[1, 2, 3, 4, 5, 6].map(y => (
                          <option key={y} value={y}>Year {y}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">School Class</label>
                      <input
                        type="text"
                        value={child.school_class}
                        onChange={(e) => handleChildChange(index, 'school_class', e.target.value)}
                        className="input-artsy"
                        placeholder="e.g., 3B, Oak Class"
                        data-testid={`child-${index}-class`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-body font-semibold text-slate-700 mb-1">Emergency Contact *</label>
                      <input
                        type="text"
                        value={child.emergency_contact}
                        onChange={(e) => handleChildChange(index, 'emergency_contact', e.target.value)}
                        className="input-artsy"
                        placeholder="Name and phone number"
                        required
                        data-testid={`child-${index}-emergency`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-body font-semibold text-slate-700 mb-1">Medical Conditions / Allergies</label>
                      <textarea
                        value={child.medical_conditions}
                        onChange={(e) => handleChildChange(index, 'medical_conditions', e.target.value)}
                        className="input-artsy"
                        rows="2"
                        placeholder="Please list any conditions we should be aware of"
                        data-testid={`child-${index}-medical`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={child.first_aid_permission}
                        onChange={(e) => handleChildChange(index, 'first_aid_permission', e.target.checked)}
                        className="w-5 h-5 rounded border-slate-300"
                        data-testid={`child-${index}-firstaid`}
                      />
                      <span className="font-body text-slate-700">I give permission for basic first aid to be administered</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={child.photo_permission}
                        onChange={(e) => handleChildChange(index, 'photo_permission', e.target.checked)}
                        className="w-5 h-5 rounded border-slate-300"
                        data-testid={`child-${index}-photo`}
                      />
                      <span className="font-body text-slate-700">I give permission for photos to be taken for promotional purposes</span>
                    </label>
                  </div>

                  {/* Returning Student Option */}
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={child.is_returning}
                        onChange={(e) => handleChildChange(index, 'is_returning', e.target.checked)}
                        className="w-5 h-5 rounded border-slate-300"
                        data-testid={`child-${index}-returning`}
                      />
                      <div>
                        <span className="font-body font-semibold text-slate-900">Returning Student</span>
                        <p className="font-body text-sm text-slate-600">Has this child attended Artsy Craftsy before? (Special rate: £62/term)</p>
                      </div>
                    </label>
                  </div>
                </div>
              ))}

              {/* Order Summary */}
              <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-200">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">Order Summary</h3>
                <div className="space-y-2 font-body">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Class</span>
                    <span className="text-slate-900">{selectedClassData?.day_of_week}s - {selectedClassData?.time}</span>
                  </div>
                  {children.map((child, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-slate-600">
                        {child.name || `Child ${idx + 1}`}
                        {child.is_returning && <span className="text-blue-600 text-xs ml-1">(Returning)</span>}
                        {!child.is_returning && idx > 0 && <span className="text-green-600 text-xs ml-1">(Sibling)</span>}
                      </span>
                      <span className="text-slate-900">
                        £{child.is_returning ? RETURNING_PRICE : (idx === 0 ? selectedClassData?.total_price : selectedClassData?.sibling_total_price)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between font-heading font-bold">
                      <span className="text-slate-900">Total</span>
                      <span className="text-blue-600 text-xl">£{calculateTotal()}</span>
                    </div>
                  </div>
                </div>
                <p className="font-body text-sm text-blue-600 mt-2">
                  <Check className="w-4 h-4 inline mr-1" />
                  Includes starter art pack for each child
                </p>
              </div>

              <button
                onClick={handleEnroll}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="complete-enrollment"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Proceed to Payment <ArrowRight className="w-5 h-5" /></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
